import { colors } from '@lmc/common/constants'
import {
  ClockIcon,
  DocumentIcon,
  EditIcon,
  ExternalLinkIcon,
  FlagIcon,
  LocationIcon
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { Avatar } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import GeoLocation from '../../../../components/GeoLocation/GeoLocation'
import { NAMESPACE } from '../../../../locale'
import { logsConstants } from '../../../../locale/logs'
import locationDisabledImage from './images/location-disabled.png'

const { color } = cssVars

const GPS_HELP_LINK =
  'https://support.logmycare.co.uk/en/articles/6418064-why-can-t-i-see-gps-locations-on-some-logs'

const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm'

export interface FooterPerson {
  name: string | { first: string; last: string }
  picture?: unknown
}

interface LogViewFooterProps {
  isNextHandover?: boolean
  completedByText?: string
  timeLogged?: string | Date
  editedOn?: string
  loggedBy?: FooterPerson
  witnessedBy?: FooterPerson
  editedBy?: FooterPerson
  showLocation?: boolean
  latitude?: number
  longitude?: number
  accuracy?: number
  activityExtra?: ReactNode
  media?: ReactNode
  goals?: ReactNode
  testId?: string
}

export const fullName = (person?: FooterPerson) => {
  if (!person) return ''
  return typeof person.name === 'string'
    ? person.name
    : `${person.name.first} ${person.name.last}`
}

export const FooterRow: FC<{
  icon: ReactNode
  label?: string
  value: ReactNode
  children?: ReactNode
  testId: string
}> = ({ icon, label, value, children, testId }) => (
  <Row data-testid={testId}>
    <IconSlot>{icon}</IconSlot>
    <Content>
      {label && <Label>{label}</Label>}
      <Value $emphasised={Boolean(label)}>{value}</Value>
      {children}
    </Content>
  </Row>
)

export const PersonAvatar: FC<{ person: FooterPerson }> = ({ person }) => (
  <Avatar
    url={typeof person.picture === 'string' ? person.picture : undefined}
    size={24}
  />
)

const LogViewFooter: FC<LogViewFooterProps> = ({
  isNextHandover,
  completedByText,
  timeLogged,
  editedOn,
  loggedBy,
  witnessedBy,
  editedBy,
  showLocation,
  latitude,
  longitude,
  accuracy,
  activityExtra,
  media,
  goals,
  testId = 'log-view-footer'
}) => {
  const { t } = useTranslation(NAMESPACE.LOGS)
  const hasGps = typeof latitude === 'number' && typeof longitude === 'number'

  return (
    <div data-testid={testId}>
      <Section data-testid={`${testId}-activity`}>
        <SectionTitle>{t(logsConstants.LOG_VIEW_LOG_ACTIVITY)}</SectionTitle>
        <Rows>
          {isNextHandover && (
            <FooterRow
              icon={<FlagIcon size={24} />}
              value={t(logsConstants.LOG_VIEW_FLAGGED_FOR_HANDOVER)}
              testId={`${testId}-handover`}
            />
          )}
          {completedByText && (
            <FooterRow
              icon={<DocumentIcon size={24} />}
              value={completedByText}
              testId={`${testId}-completed-by`}
            />
          )}
          {timeLogged && (
            <FooterRow
              icon={<ClockIcon size={24} />}
              label={t(logsConstants.TIME_LOGGED)}
              value={dayjs(timeLogged).format(DATE_TIME_FORMAT)}
              testId={`${testId}-time-logged`}
            />
          )}
          {loggedBy && (
            <FooterRow
              icon={<PersonAvatar person={loggedBy} />}
              label={t(logsConstants.LOG_VIEW_LOGGED_BY)}
              value={fullName(loggedBy)}
              testId={`${testId}-logged-by`}
            />
          )}
          {witnessedBy && (
            <FooterRow
              icon={<PersonAvatar person={witnessedBy} />}
              label={t(logsConstants.LOG_VIEW_WITNESSED_BY)}
              value={fullName(witnessedBy)}
              testId={`${testId}-witnessed-by`}
            />
          )}
          {editedOn && (
            <FooterRow
              icon={<EditIcon size={24} />}
              label={t(logsConstants.LOG_VIEW_LAST_EDITED)}
              value={dayjs(editedOn).format(DATE_TIME_FORMAT)}
              testId={`${testId}-last-edited`}
            />
          )}
          {editedBy && (
            <FooterRow
              icon={<PersonAvatar person={editedBy} />}
              label={t(logsConstants.LOG_VIEW_EDITED_BY)}
              value={fullName(editedBy)}
              testId={`${testId}-edited-by`}
            />
          )}
          {activityExtra}
        </Rows>
      </Section>
      {media && <Section>{media}</Section>}
      {goals && <Section>{goals}</Section>}
      {showLocation && (
        <Section data-testid={`${testId}-location`}>
          <FooterRow
            icon={
              <LocationIconSlot $hasGps={hasGps}>
                <LocationIcon size={24} />
              </LocationIconSlot>
            }
            label={t(logsConstants.LOG_VIEW_LOCATION)}
            value={hasGps ? null : t(logsConstants.LOG_VIEW_GPS_DISABLED)}
            testId={`${testId}-location-row`}
          >
            {!hasGps && (
              <LearnMore
                href={GPS_HELP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testId}-learn-more`}
              >
                {t(logsConstants.LOG_VIEW_LEARN_ABOUT_GPS)}
                <ExternalLinkIcon size={16} />
              </LearnMore>
            )}
          </FooterRow>
          {hasGps ? (
            <GeoLocation
              latitude={latitude}
              longitude={longitude}
              accuracy={accuracy}
            />
          ) : (
            <DisabledMap
              src={locationDisabledImage}
              alt={t(logsConstants.LOG_VIEW_GPS_DISABLED_IMAGE)}
              data-testid={`${testId}-gps-disabled-map`}
            />
          )}
        </Section>
      )}
    </div>
  )
}

export default LogViewFooter

export const Section = styled.section`
  border-top: 1px solid ${colors.lightgrey};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

export const SectionTitle = styled.h3`
  color: ${colors.black};
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Row = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
`

const IconSlot = styled.span`
  align-items: center;
  color: ${colors.darkgrey};
  display: flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;

  & > * {
    display: block;
    flex-shrink: 0;
  }
`

const LocationIconSlot = styled.span<{ $hasGps: boolean }>`
  color: ${({ $hasGps }) =>
    $hasGps ? color.text.action.primary.default : colors.darkgrey};
  display: flex;
`

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-height: 24px;
  justify-content: center;
  min-width: 0;
`

const Label = styled.span`
  color: ${colors.darkgrey};
  font-size: 14px;
  line-height: 24px;
`

const Value = styled.span<{ $emphasised: boolean }>`
  color: ${colors.black};
  font-size: 14px;
  font-weight: ${({ $emphasised }) => ($emphasised ? 600 : 400)};

  &:empty {
    display: none;
  }
`

const LearnMore = styled.a`
  align-items: center;
  align-self: flex-start;
  color: ${color.text.link.primary.default};
  display: inline-flex;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  margin-top: 4px;
  text-decoration: underline;
`

const DisabledMap = styled.img`
  align-self: flex-start;
  aspect-ratio: 3 / 2;
  border-radius: 8px;
  max-width: 320px;
  object-fit: cover;
  width: 100%;
`
